import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Wrench } from 'lucide-react';

import { Card, CardContent } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { useCopyToolFromDefault } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useToast } from 'hooks/use-toast';
import {
  BlocksToolCard,
  BlocksToolsListSkeleton,
  ViewMode,
  toolsGridClassName,
  toolsListPaginationClassName,
  toolsListPaginationControlsClassName,
  toolsSectionHeadingClassName,
} from './tools-shared';
import { ToolsPagination } from './tools-pagination';
import { useTranslation } from 'react-i18next';

function NoBlocksTool({ isLoading = false, isAdmin = false }: { isLoading?: boolean; isAdmin?: boolean }) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Card className="border-none p-0 shadow-none">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Skeleton className="mb-6 h-20 w-20 rounded-full" />
          <Skeleton className="mb-2 h-7 w-40" />
          <Skeleton className="mb-2 h-5 w-80" />
          <Skeleton className="mb-8 h-5 w-72" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-none p-0 shadow-none">
      <CardContent className="flex flex-col items-center justify-center py-12">
        <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
          <Wrench className="h-10 w-10 text-muted-foreground" />
        </div>

        <h3 className="mb-2 text-xl font-semibold">{t('NO_BLOCKS_TOOLS')}</h3>
        <p className="mb-8 text-center text-base leading-relaxed text-muted-foreground">
          {isAdmin
            ? t('NO_BLOCKS_TOOLS_ADMIN_HINT') ||
              "You haven't created any Blocks tools yet. Go to Tools tab to create a Blocks tool!"
            : t('NO_BLOCKS_TOOLS_EMPTY_HINT')}
        </p>
      </CardContent>
    </Card>
  );
}

export function BlocksToolsList({
  data,
  isLoading,
  isAdmin,
  view,
  projectKey,
  onToolSelect,
  onDeleteTool,
  isDeletingToolId,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
}: {
  data?: { tools: ITool[]; total_count: number; total_pages: number; page: number };
  isLoading: boolean;
  isAdmin: boolean;
  view: ViewMode;
  projectKey: string;
  onToolSelect: (tool: ITool) => void;
  onDeleteTool?: (tool: ITool) => void;
  isDeletingToolId?: string | null;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions: number[];
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const copyToolMutation = useCopyToolFromDefault();
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [addingToolId, setAddingToolId] = useState<string | null>(null);
  const tools = data?.tools || [];
  const totalCount = data?.total_count || 0;
  const pageCount = Math.max(1, data?.total_pages ?? 1);
  const isNoTools = !isLoading && data && totalCount === 0;
  const { t } = useTranslation();

  const handleAddTool = async (tool: ITool) => {
    if (!projectKey) {
      toast({
        variant: 'destructive',
        title: t('ADD_FAILED'),
        description:
          t('PROJECT_KEY_MISSING_REFRESH'),
      });
      return;
    }

    setAddingToolId(tool.id);

    try {
      const result = await copyToolMutation.mutateAsync({
        defaultToolId: tool.id,
        projectKey,
      });

      if (result.is_success === false) {
        throw new Error(
          result.message || result.detail || t('FAILED_TO_ADD_TOOL')
        );
      }

      toast({
        variant: 'success',
        title: t('TOOL_ADDED'),
        description:
          t('TOOL_ADDED_SUCCESSFULLY', { name: tool.name }),
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tools'] }),
        queryClient.invalidateQueries({ queryKey: ['tools', 'open-tools'] }),
      ]);

      setSelectedCardId(null);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ADD_FAILED'),
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_ADD_TOOL'),
      });
    } finally {
      setAddingToolId(null);
    }
  };

  return (
    <>
      {isNoTools ? (
        <NoBlocksTool isAdmin={isAdmin} />
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          {isLoading ? <BlocksToolsListSkeleton view={view} /> : null}

          {!isLoading ? (
            <>
              <p className={toolsSectionHeadingClassName}>{t('BLOCKS_TOOLS') || 'Blocks Tools'}</p>
              <div className={view === 'grid' ? toolsGridClassName : 'space-y-3'}>
              {tools.map((tool) => (
                <BlocksToolCard
                  key={tool.id}
                  tool={tool}
                  view={view}
                  isSelected={selectedCardId === tool.id}
                  allowEdit={isAdmin}
                  allowDelete={isAdmin}
                  onDelete={onDeleteTool}
                  isDeleting={isDeletingToolId === tool.id}
                  isAdding={addingToolId === tool.id}
                  onSelect={(selected) => {
                    if (isAdmin) {
                      onToolSelect(selected);
                      return;
                    }

                    if (addingToolId) return;
                    setSelectedCardId((current) => (current === selected.id ? null : selected.id));
                  }}
                  onAdd={handleAddTool}
                  projectKey={projectKey}
                  isAdmin={isAdmin}
                />
              ))}
              </div>
            </>
          ) : null}

            {data && totalCount > 0 ? (
              <>
                <div className={toolsListPaginationClassName}>
                  <span className="text-sm text-muted-foreground">
                    {t('TOOLS_SHOWING_ENTRIES', {
                      from: (page - 1) * pageSize + 1,
                      to: Math.min(page * pageSize, totalCount),
                      total: totalCount,
                    })}
                  </span>

                  <div className={toolsListPaginationControlsClassName}>
                    <select
                      value={String(pageSize)}
                      onChange={(event) => onPageSizeChange(Number(event.target.value))}
                      className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm sm:w-auto"
                    >
                      {pageSizeOptions.map((size) => (
                        <option key={size} value={size}>
                          {t('TOOLS_PER_PAGE', { size })}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <ToolsPagination page={page} pageCount={pageCount} onPageChange={onPageChange} />
              </>
            ) : null}
        </div>
      )}
    </>
  );
}
